import { ShieldCheck, CheckCircle, DollarSign, Home, ThumbsUp, Ruler, CalendarClock, Hammer, Users, Wrench } from 'lucide-react';

import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/adu.js';



export default function ADUPage() {
  const service = {
    title: 'ADU / Garage Conversion',
    image: '/hero/best-adu-garage-conversion-remodeling-in-los-angeles.webp',
    description: 'Garage & ADU conversions by Modern Build. Fully permitted, livable spaces from guest houses to home offices. Free estimate: (323) 300-4130',
    link: '/services/adu',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));


    // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "ADU Services", "item": pageUrl }
    ]
  };


  return (
    <div className="bg-white">
    <>
      <Head>
        <title>ADU / Garage Conversion Services | Free Estimate | Modern Build</title>
        <meta name="title" content="ADU / Garage Conversion Services | Free Estimate | Modern Build" />
        <meta name="description" content="Garage & ADU conversions by Modern Build. Fully permitted, livable spaces from guest houses to home offices. Free estimate: (323) 300-4130" />
        <meta name="keywords" content="ADU conversion, garage conversion, los angeles ADU builder, garage remodel, accessory dwelling unit, guest house conversion, ADU permits, Modern Build" />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/adu" />

        <meta property="og:title" content="ADU / Garage Conversion Services | Free Estimate | Modern Build" />
        <meta property="og:description" content="Garage & ADU conversions by Modern Build. Fully permitted, livable spaces from guest houses to home offices. Free estimate: (323) 300-4130" />
        <meta property="og:image" content="https://modernbuildremodel.com//hero/best-adu-garage-conversion-remodeling-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/services/adu" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ADU / Garage Conversion Services | Free Estimate | Modern Build" />
        <meta name="twitter:description" content="Garage & ADU conversions by Modern Build. Fully permitted, livable spaces from guest houses to home offices. Free estimate: (323) 300-4130" />
        <meta name="twitter:image" content="https://modernbuildremodel.com//hero/best-adu-garage-conversion-remodeling-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateWebPageSchema({
            title: 'ADU / Garage Conversion Services | Free Estimate | Modern Build',
            description: 'Garage and ADU conversion experts in Los Angeles. Modern Build creates fully permitted, livable spaces tailored to your needs — from guest houses to home offices.',
            slug: '/services/adu',
          }))
        }} />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>


      <Header />
      <Hero category="adu" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="ADU & GARAGE CONVERSION SERVICES"
        title="Transform Unused Space into Livable Value"
        background="white"
        description="Modern Build specializes in garage and ADU conversions in Los Angeles. Whether you're creating a rental unit, guest house, or home office, we manage the full process — from permits to construction — with unmatched attention to detail."
        extraText="Our team ensures your conversion meets all zoning, safety, and design standards, so you get a fully legal, functional space."
        phone="(323) 300-4130"
        image1="/split/adu-garage-conversion-los-angeles.webp"
        image2="/split/adu-garage-construction-los-angeles.webp"
      />

      <Benefits
        title="What’s Included in an ADU Conversion?"
        subtitle="Everything you need for a legal, comfortable ADU"
        items={[
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Garage-to-Living Space Conversion",
          },
          {
            icon: <Ruler className="w-8 h-8 text-primary" />,
            title: "Custom Floorplans & Design",
          },
          {
            icon: <Wrench className="w-8 h-8 text-primary" />,
            title: "Plumbing & Electrical Installation",
          },
          {
            icon: <Hammer className="w-8 h-8 text-primary" />,
            title: "Framing, Drywall & Insulation",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "City Permits & Compliance",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Energy-Efficient Windows & HVAC",
            href: "/services/energy-efficient-windows",
          },
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Work Performed by Licensed, Insured Contractors",
            href: "/about-us",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Financing Options Available",
            href: "/financing",
          },
        ]}
      />
      <ContentBlock
        subtitle="LOS ANGELES ADU EXPERTS"
        title="Why Convert Your Garage?"
        description="ADUs and garage conversions add living space and long-term value to your home. In Los Angeles, these projects are increasingly popular for passive income, multi-generational living, or flexible use like a home office or gym."
        extraText="Modern Build handles zoning checks, design, city approvals, and full construction — giving you a turnkey experience from day one."
        buttonText="Schedule a Free Consultation"
        background="white"
      />

      <ProcessRoadmap
        title="Our ADU / Garage Conversion Process"
        subtitle="From concept to completion"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Free Property Assessment",
            description: "We evaluate your garage and zoning to confirm feasibility for ADU conversion.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Custom Design & Engineering",
            description: "Our team creates a functional, beautiful floor plan and handles permit drawings.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Permit Approval & Construction",
            description: "We submit plans to the city and begin full ADU construction with weekly updates.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Walkthrough & Move-In",
            description: "You inspect the finished space, and we handle city sign-off and occupancy clearance.",
          },
        ]}
      />

      <Split
        subtitle="A FUNCTIONAL SPACE YOU’LL LOVE"
        title="Make the Most of What You Already Have"
        background="white"
        description="Garage conversions are one of the most cost-effective ways to expand your home’s usable square footage. We help you maximize comfort, code compliance, and long-term value — all in a streamlined, professionally managed process."
        extraText="Modern Build is your trusted partner for ADU conversions across Los Angeles County."
        phone="(323) 300-4130"
        image1="/split/adu-living-space-los-angeles.webp"
        image2="/split/adu-garage-addition-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Start Your ADU Conversion Today"
        subtitle="Free Property Review + Flexible Financing"
        buttonText="Get Your Free Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="adu" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  </div>
  );
}
